import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Checkbox,
  TextField,
  Grid,
  Select,
  MenuItem,
} from "@mui/material";
import {
  createPickupOrder,
  getProcurementsForPlant,
} from "../../api/biomassProcurement";
import { useAlert } from "../../components/Alert";
import { useTranslation } from "react-i18next";
import CustomAppBar from "../../components/AppBar";
import { getUserBasicDetailFromAuth } from "../../utils/common";
import { getAreaManagerVles } from "../../api/userManagement";
import { getBiocharPlants } from "../../api/biocharPlant";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import { useNavigate } from "react-router-dom";

const ProcurementsTable = ({
  procurements,
  selectedProcurements,
  handleSelect,
  step,
  t,
}) => {
  let isSummary = step === 2;
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {!isSummary && <TableCell>{t("Select")}</TableCell>}
            <TableCell>{t("Farmer Name")}</TableCell>
            <TableCell>{t("Village")}</TableCell>
            <TableCell>{t("Quantity")}</TableCell>
            <TableCell>{t("Amount")}</TableCell>
            <TableCell>{t("Inspected By / On")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {procurements.map((proc) => {
            let showRow = true;
            if (isSummary) {
              showRow = selectedProcurements.includes(proc.id);
            }
            return (
              <>
                {showRow ? (
                  <TableRow key={proc.id}>
                    {!isSummary && (
                      <TableCell>
                        <Checkbox
                          checked={selectedProcurements.includes(proc.id)}
                          onChange={() => handleSelect(proc.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>{proc.farmer_name}</TableCell>
                    <TableCell>{proc.village}</TableCell>
                    <TableCell>{proc.quantity_kg}</TableCell>
                    <TableCell>{proc.estimated_payout}</TableCell>
                    <TableCell>{proc.inspection_date}</TableCell>
                  </TableRow>
                ) : null}
              </>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const CreatePickupOrder = () => {
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { full_name, app_user_id } = getUserBasicDetailFromAuth();

  const [biocharPlants, setBiocharPlants] = useState([]);
  const [procurements, setProcurements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProcurements, setSelectedProcurements] = useState([]);
  const [assignedToUsers, setAssignedToUsers] = useState([
    {
      id: app_user_id,
      name: `${full_name} (${t("Self")})`,
    },
  ]);
  const [step, setStep] = useState(1);
  const [pickupDetails, setPickupDetails] = useState({
    plant: "",
    assignedTo: full_name,
    transportName: "",
    pickupDate: "",
  });

  const fetchBiocharPlants = async () => {
    setIsLoading(true);
    try {
      const response = await getBiocharPlants();
      setBiocharPlants(response || []);
    } catch (error) {
      console.error("Error fetching Biochar plants", error);
      showAlert("error", "Failed to fetch Biochar plants");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProcurements = async (plant) => {
    setIsLoading(true);
    try {
      const response = await getProcurementsForPlant(plant);
      setProcurements(response || []);
    } catch (error) {
      console.error("Error fetching procurements:", error);
      showAlert("error", t("Failed to fetch procurements"));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignedToUsers = async () => {
    try {
      const response = await getAreaManagerVles(app_user_id);
      setAssignedToUsers([
        {
          id: app_user_id,
          name: `${full_name} (${t("Self")})`,
        },
        ...(response.data.vles || []).map((user) => ({
          id: user.id,
          name: `${user.full_name} (${t("VLE")})`,
        })),
      ]);
    } catch (error) {
      console.error("Error fetching assigned to users:", error);
      showAlert("error", t("Failed to fetch assigned to users"));
    }
  };

  const handleSelect = (id) => {
    setSelectedProcurements((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleNext = () => {
    if (selectedProcurements.length > 0) {
      setStep(2);
    }
  };

  const handleChange = (e) => {
    setPickupDetails({ ...pickupDetails, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      let request_data = {
        procurements: selectedProcurements,
        plant: pickupDetails.plant,
        pickup_assigned_to: pickupDetails.assignedTo,
        transport_name: pickupDetails.transportName,
        pickup_date: pickupDetails.pickupDate,
      };
      await createPickupOrder(request_data);
      showAlert("success", t("Pickup order created successfully!"));
      navigate("/pickup-orders");
    } catch (error) {
      console.error("Error creating pickup order:", error);
      showAlert("error", t("Failed to create pickup order"));
    }
  };

  const handlePlantChange = (e) => {
    setPickupDetails({ ...pickupDetails, plant: e.target.value });
    fetchProcurements(e.target.value);
  };

  useEffect(() => {
    fetchBiocharPlants();
    fetchAssignedToUsers();
  }, []);

  return (
    <>
      <CustomAppBar />
      <Paper sx={{ padding: 3, maxWidth: 900, margin: "auto", mt: 5 }}>
        {isLoading && <p>{t("Loading...")}</p>}
        {step === 1 && !isLoading && (
          <>
            <h2>{t("Schedule Pickup")}</h2>
            <Select
              label={t("Inward Plant")}
              name="pickup_plant"
              value={pickupDetails.plant}
              onChange={handlePlantChange}
              fullWidth
            >
              {biocharPlants.map((plant) => (
                <MenuItem key={plant.id} value={plant.id}>
                  {plant.name}
                </MenuItem>
              ))}
            </Select>
            {isNotNullOrEmptyArray(procurements) ? (
              <>
                <ProcurementsTable
                  procurements={procurements}
                  selectedProcurements={selectedProcurements}
                  handleSelect={handleSelect}
                  step={step}
                  t={t}
                />
                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={handleNext}
                  disabled={selectedProcurements.length === 0}
                >
                  {t("Schedule Pickup")}
                </Button>
              </>
            ) : (
              <p>{t("No procurements found")}</p>
            )}
          </>
        )}
        {step === 2 && (
          <>
            <h2>{t("Pickup Summary")}</h2>
            <ProcurementsTable
              procurements={procurements}
              selectedProcurements={selectedProcurements}
              handleSelect={handleSelect}
              step={step}
              t={t}
            />
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <Select
                  label={t("Assigned to (Self/VLE)")}
                  name="assignedTo"
                  value={pickupDetails.assignedTo}
                  onChange={handleChange}
                  fullWidth
                >
                  {assignedToUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("Transport Name")}
                  name="transportName"
                  value={pickupDetails.transportName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label={t("Pickup Date")}
                  name="pickupDate"
                  InputLabelProps={{ shrink: true }}
                  value={pickupDetails.pickupDate}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setStep(1)}
                  fullWidth
                >
                  {t("Back")}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  fullWidth
                >
                  {t("Submit")}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
    </>
  );
};

export default CreatePickupOrder;
